<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import ContentCard from 'dashboard/components-next/teletel/ContentCard.vue';
import { visibleTeletelPermissionGroups } from 'dashboard/helper/teletelCallCenter';

/**
 * Teletel roles management: create/edit/delete custom roles built from the
 * Teletel permission keys, and assign them to agents. Administrators only -
 * the server enforces the same rule on every endpoint.
 */
const { t } = useI18n();
const store = useStore();

// Grouped permission keys (batches 0 + 1 + 2). Labels live in i18n.
const GROUPS = [
  {
    key: 'CONVERSATIONS',
    keys: [
      'conversation_manage',
      'conversation_unassigned_manage',
      'conversation_participating_manage',
      'conversation_assigned_scope_only',
    ],
  },
  {
    key: 'CRM',
    keys: [
      'contact_manage',
      'lifecycle_manage',
      'report_manage',
      'knowledge_base_manage',
    ],
  },
  { key: 'FLOWS', keys: ['flow_trigger', 'flow_manage', 'flow_template_use'] },
  {
    key: 'GOVERNANCE',
    keys: ['sla_manage', 'audit_view', 'supervisor_manage', 'assistant_manage'],
  },
  { key: 'CALL_CENTER', keys: ['call_center_manage'] },
  {
    key: 'CORE',
    keys: [
      'campaign_manage',
      'inbox_manage',
      'automation_manage',
      'label_manage',
      'team_manage',
      'agent_manage',
      'custom_attribute_manage',
      'integration_manage',
      'macro_manage',
    ],
  },
];

const roles = computed(() => store.getters['teletelRoles/getRoles']);
const uiFlags = computed(() => store.getters['teletelRoles/getUIFlags']);
const agents = computed(() => store.getters['agents/getAgents']);
const visibleGroups = computed(() => visibleTeletelPermissionGroups(GROUPS));

const editorOpen = ref(false);
const editorCard = ref(null);
const editing = reactive({
  id: null,
  name: '',
  description: '',
  permissions: [],
});
const assignment = reactive({ userId: '', roleId: '' });

onMounted(() => {
  store.dispatch('teletelRoles/get');
  store.dispatch('agents/get');
});

const openEditor = async (role = null) => {
  editing.id = role?.id || null;
  editing.name = role?.name || '';
  editing.description = role?.description || '';
  editing.permissions = [...(role?.permissions || [])];
  editorOpen.value = true;
  await nextTick();
  editorCard.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const togglePermission = key => {
  const index = editing.permissions.indexOf(key);
  if (index === -1) editing.permissions.push(key);
  else editing.permissions.splice(index, 1);
};

const saveRole = async () => {
  try {
    const payload = {
      name: editing.name,
      description: editing.description,
      permissions: editing.permissions,
    };
    if (editing.id) {
      await store.dispatch('teletelRoles/update', {
        id: editing.id,
        ...payload,
      });
    } else {
      await store.dispatch('teletelRoles/create', payload);
    }
    editorOpen.value = false;
    useAlert(t('TELETEL_ROLES.ACTIONS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TELETEL_ROLES.ACTIONS.ERROR')
    );
  }
};

const deleteRole = async role => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(t('TELETEL_ROLES.LIST.DELETE_CONFIRM', { name: role.name }))
  ) {
    return;
  }
  try {
    await store.dispatch('teletelRoles/delete', role.id);
    useAlert(t('TELETEL_ROLES.ACTIONS.DELETED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TELETEL_ROLES.ACTIONS.ERROR')
    );
  }
};

const assignRole = async () => {
  if (!assignment.userId) return;
  try {
    await store.dispatch('teletelRoles/assign', {
      userId: assignment.userId,
      customRoleId: assignment.roleId || null,
    });
    useAlert(t('TELETEL_ROLES.ASSIGN.DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TELETEL_ROLES.ACTIONS.ERROR')
    );
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 p-4 overflow-auto">
    <PageHeader
      :title="t('TELETEL_ROLES.HEADER')"
      :description="t('TELETEL_ROLES.DESCRIPTION')"
    />

    <ContentCard :title="t('TELETEL_ROLES.LIST.TITLE')">
      <div class="flex justify-end mb-2">
        <NextButton sm @click="openEditor()">
          {{ t('TELETEL_ROLES.LIST.NEW') }}
        </NextButton>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-start text-n-slate-11">
            <th class="py-2 text-start">{{ t('TELETEL_ROLES.LIST.NAME') }}</th>
            <th class="text-start">
              {{ t('TELETEL_ROLES.LIST.PERMISSIONS') }}
            </th>
            <th class="text-start">{{ t('TELETEL_ROLES.LIST.ASSIGNED') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="role in roles"
            :key="role.id"
            class="border-t border-n-weak"
          >
            <td class="py-2">
              <p class="font-medium text-n-slate-12">{{ role.name }}</p>
              <p class="text-xs text-n-slate-10">{{ role.description }}</p>
            </td>
            <td>{{ role.permissions.length }}</td>
            <td>{{ role.assigned_users_count }}</td>
            <td class="text-end">
              <button
                type="button"
                class="text-n-brand me-2"
                @click="openEditor(role)"
              >
                {{ t('TELETEL_ROLES.LIST.EDIT') }}
              </button>
              <button
                type="button"
                class="text-n-ruby-11"
                @click="deleteRole(role)"
              >
                {{ t('TELETEL_ROLES.LIST.DELETE') }}
              </button>
            </td>
          </tr>
          <tr v-if="!roles.length && !uiFlags.isFetching">
            <td colspan="4" class="py-4 text-center text-n-slate-10">
              {{ t('TELETEL_ROLES.LIST.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </ContentCard>

    <ContentCard
      v-if="editorOpen"
      ref="editorCard"
      :title="t('TELETEL_ROLES.FORM.TITLE')"
    >
      <div class="flex flex-col gap-3 text-sm">
        <label>{{ t('TELETEL_ROLES.FORM.NAME') }}</label>
        <input v-model="editing.name" class="w-72 mb-0" />
        <label>{{ t('TELETEL_ROLES.FORM.ROLE_DESCRIPTION') }}</label>
        <input v-model="editing.description" class="w-96 mb-0" />

        <div
          v-for="group in visibleGroups"
          :key="group.key"
          class="flex flex-col gap-1"
        >
          <p class="font-medium text-n-slate-12">
            {{ t(`TELETEL_ROLES.GROUPS.${group.key}`) }}
          </p>
          <label
            v-for="key in group.keys"
            :key="key"
            class="flex items-center gap-2"
          >
            <input
              type="checkbox"
              class="mb-0"
              :checked="editing.permissions.includes(key)"
              @change="togglePermission(key)"
            />
            {{ t(`TELETEL_ROLES.PERMISSIONS.${key}`) }}
          </label>
        </div>

        <div class="flex gap-2">
          <NextButton
            sm
            :is-loading="uiFlags.isSaving"
            :disabled="!editing.name.trim()"
            @click="saveRole"
          >
            {{ t('TELETEL_ROLES.FORM.SAVE') }}
          </NextButton>
          <NextButton sm slate @click="editorOpen = false">
            {{ t('TELETEL_ROLES.FORM.CANCEL') }}
          </NextButton>
        </div>
      </div>
    </ContentCard>

    <ContentCard :title="t('TELETEL_ROLES.ASSIGN.TITLE')">
      <p class="mb-2 text-xs text-n-slate-10">
        {{ t('TELETEL_ROLES.ASSIGN.HINT') }}
      </p>
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <select v-model="assignment.userId" class="w-56 mb-0">
          <option value="" disabled>
            {{ t('TELETEL_ROLES.ASSIGN.PICK_AGENT') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <select v-model="assignment.roleId" class="w-56 mb-0">
          <option value="">
            {{ t('TELETEL_ROLES.ASSIGN.NO_ROLE') }}
          </option>
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.name }}
          </option>
        </select>
        <NextButton sm :disabled="!assignment.userId" @click="assignRole">
          {{ t('TELETEL_ROLES.ASSIGN.APPLY') }}
        </NextButton>
      </div>
    </ContentCard>
  </div>
</template>
